import React from 'react';
import { Link } from 'react-router-dom';
import { Printer, Phone, MapPin, Mail, Clock, Facebook, Instagram, Linkedin, ArrowRight } from 'lucide-react';

const Footer = () => {
  return (
    <footer className="footer-modern text-white">
      <div className="container mx-auto px-4 py-16">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
          {/* Brand Section */}
          <div className="col-span-1 md:col-span-2 space-y-6">
            <div className="flex items-center space-x-3">
              <div className="bg-primary-pink p-3 rounded-2xl">
                <Printer className="w-7 h-7 text-white" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-primary-pink">DigiPrint Numérique</h3>
                <p className="text-neutral-400 font-medium">Imprimerie Professionnelle Abidjan</p>
              </div>
            </div>
            
            <p className="text-neutral-300 leading-relaxed max-w-lg">
              Depuis 2008, votre partenaire de confiance pour tous vos besoins d'impression numérique et offset. 
              Nous allions savoir-faire artisanal et technologies de pointe pour des résultats d'exception.
            </p>
            
            <div className="flex space-x-4">
              {[
                { icon: <Facebook className="w-5 h-5" />, href: "#", label: "Facebook" },
                { icon: <Instagram className="w-5 h-5" />, href: "#", label: "Instagram" },
                { icon: <Linkedin className="w-5 h-5" />, href: "#", label: "LinkedIn" }
              ].map((social, index) => (
                <a
                  key={index}
                  href={social.href}
                  aria-label={social.label}
                  className="bg-neutral-800 hover:bg-primary-pink p-3 rounded-xl transition-all duration-300 interactive-element"
                >
                  {social.icon}
                </a>
              ))}
            </div>
          </div>

          {/* Services Navigation */}
          <div className="space-y-6">
            <h4 className="text-lg font-semibold text-white">Nos Services</h4>
            <ul className="space-y-3">
              {[
                { name: "Impression offset", path: "/services" },
                { name: "Impression numérique", path: "/services" },
                { name: "Cartes de visite", path: "/services" },
                { name: "Flyers & Brochures", path: "/services" },
                { name: "Affiches grand format", path: "/services" },
                { name: "Reliure professionnelle", path: "/services" }
              ].map((service, index) => (
                <li key={index}>
                  <Link
                    to={service.path}
                    className="text-neutral-300 hover:text-accent-emerald transition-colors duration-300 inline-flex items-center group"
                  >
                    <span>{service.name}</span>
                    <ArrowRight className="w-4 h-4 ml-2 opacity-0 group-hover:opacity-100 transform group-hover:translate-x-1 transition-all duration-300" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Information */}
          <div className="space-y-6">
            <h4 className="text-lg font-semibold text-white">Contact & Horaires</h4>
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <MapPin className="w-5 h-5 text-accent-emerald mt-1 flex-shrink-0" />
                <div>
                  <p className="text-neutral-200 font-medium">DIGIPRINT SARL Cocody II Plateaux Vallons,Rue des Jardins en face de la Station Corlay,Abidjan</p>
                  <p className="text-neutral-400 text-sm">Côte d'Ivoire</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-3">
                <Phone className="w-5 h-5 text-accent-emerald mt-1 flex-shrink-0" />
                <div className="text-neutral-200">
                  <a 
                    href="tel:+2250787635341"
                    className="block hover:text-accent-emerald transition-colors mb-1"
                  >
                    +2250787635341
                  </a>
                </div>
              </div>
              
              <div className="flex items-center space-x-3">
                <Mail className="w-5 h-5 text-accent-emerald flex-shrink-0" />
                <a 
                  href="mailto:infos@digiprintnumerique.com"
                  className="text-neutral-200 hover:text-accent-emerald transition-colors"
                >
                  infos@digiprintnumerique.com
                </a>
              </div>
              
              <div className="flex items-start space-x-3">
                <Clock className="w-5 h-5 text-accent-emerald mt-1 flex-shrink-0" />
                <div className="text-neutral-200">
                  <p className="font-medium">Lun-Ven: 8h30-17h00</p>
                  <p>Sam: 8h00-13h00</p>
                  <p className="text-neutral-400">Dim: Fermé</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="surface-elevated bg-gradient-to-r from-primary-stone to-primary-stone-light p-8 rounded-3xl mb-12">
          <div className="text-center space-y-6">
            <h3 className="text-2xl font-bold text-white">Prêt pour votre prochain projet ?</h3>
            <p className="text-neutral-200 max-w-2xl mx-auto">
              Obtenez un devis personnalisé sous 24h et découvrez comment nous pouvons 
              transformer vos idées en impressions d'exception.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <a
                href="https://wa.me/2250787635341?text=Bonjour%20DigiPrint,%20j'aimerais%20obtenir%20un%20devis%20pour%20mon%20projet%20d'impression."
                target="_blank"
                rel="noopener noreferrer"
                className="btn-accent px-8 py-4 inline-flex items-center justify-center space-x-3"
              >
                <span>WhatsApp Direct</span>
                <ArrowRight className="w-5 h-5" />
              </a>
              <Link
                to="/contact"
                className="bg-white text-primary-stone hover:bg-neutral-100 px-8 py-4 rounded-2xl font-semibold transition-all duration-300 inline-flex items-center justify-center space-x-3"
                style={{
                  backgroundColor: "#878787"
                }}>
                <span>Formulaire de contact</span>
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="border-t border-neutral-700 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-neutral-400 text-center md:text-left">
              © 2024 DigiPrint Numérique. Tous droits réservés.
            </p>
            <div className="flex items-center space-x-6 text-sm text-neutral-400">
              <span>Imprimerie professionnelle à Abidjan</span>
              <span className="hidden md:inline">•</span>
              <span>Côte d'Ivoire</span>
              <span className="hidden md:inline">•</span>
              <span>Service premium depuis 2008</span>
            </div>
          </div>
        </div>

        {/* Emergency Contact Badge */}
        <div className="fixed bottom-6 right-6 z-40">
          <a
            href="https://wa.me/2250787635341?text=🚨%20URGENT%20-%20J'ai%20besoin%20d'aide%20pour%20un%20projet%20d'impression%20express"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-accent-emerald hover:bg-green-600 text-white px-4 py-3 rounded-full shadow-2xl transition-all duration-300 flex items-center space-x-2 interactive-element"
            title="Contact d'urgence 24h/24"
          >
            <Phone className="w-5 h-5" />
            <span className="hidden sm:inline font-medium">Urgence 24h</span>
          </a>
        </div>
      </div>
    </footer>
  );
};

export default Footer;